import { format } from 'date-fns';

/**
 * Escape a CSV field per RFC 4180.
 * Wraps in quotes if the value contains comma, double-quote, or newline.
 */
export const escapeCSVField = (field) => {
  if (field === null || field === undefined) return '';
  const stringValue = String(field);
  if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n') || stringValue.includes('\r')) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
};

/**
 * Helper for consistent date handling (same as Results.js getAdjustedDate)
 */
const getAdjustedDate = (dateInput) => {
  if (dateInput instanceof Date) {
    const date = new Date(dateInput);
    date.setUTCHours(12, 0, 0, 0);
    return date;
  }
  if (typeof dateInput === 'string') {
    if (dateInput.includes('T')) return new Date(dateInput);
    return new Date(`${dateInput}T12:00:00Z`);
  }
  return new Date();
};

/**
 * Helper to batch .in() queries to avoid Supabase URL length limits.
 * Fetches in chunks and concatenates results.
 */
const batchInQuery = async (supabase, table, selectClause, filterColumn, ids, options = {}) => {
  const BATCH_SIZE = 50; // Keep URL well under limits
  const allResults = [];

  for (let i = 0; i < ids.length; i += BATCH_SIZE) {
    const batch = ids.slice(i, i + BATCH_SIZE);
    let query = supabase.from(table).select(selectClause).in(filterColumn, batch);
    if (options.order) {
      query = query.order(options.order.column, { ascending: options.order.ascending });
    }
    const { data, error } = await query;
    if (error) throw error;
    if (data) allResults.push(...data);
  }

  return allResults;
};

/**
 * Fetch all data needed for bulk export across a date range.
 */
export const fetchBulkExportData = async (supabase, fromDate, toDate, { includeArchived = false } = {}) => {
  // 1. Query uploads in the date range
  let uploadQuery = supabase
    .from('uploads')
    .select('id, filename, created_at, status, provider_count, total_amount, work_week_from_date, work_week_to_date, is_archived')
    .in('status', ['processed', 'completed'])
    .gte('work_week_from_date', fromDate)
    .lte('work_week_to_date', toDate)
    .order('work_week_from_date', { ascending: true });

  if (!includeArchived) {
    uploadQuery = uploadQuery.eq('is_archived', false);
  }

  const { data: uploads, error: uploadError } = await uploadQuery;
  if (uploadError) throw uploadError;
  if (!uploads || uploads.length === 0) {
    return { uploads: [], results: [], locationRows: [], providerSummaries: {}, grandTotal: 0 };
  }

  const uploadIds = uploads.map(u => u.id);

  // 2. Batch-fetch calculation_results with provider join
  const calcResults = await batchInQuery(
    supabase,
    'calculation_results',
    `
      id,
      upload_id,
      provider_id,
      location_count,
      total_units,
      total_cost,
      pay_date,
      created_at,
      service_type_id,
      service_type_name,
      rate_type,
      billable_hours,
      hourly_rate,
      providers (
        id,
        name,
        net_terms,
        email,
        hourly_rate
      ),
      uploads (
        work_week_from_date,
        work_week_to_date
      )
    `,
    'upload_id',
    uploadIds,
    { order: { column: 'created_at', ascending: false } }
  );

  if (calcResults.length === 0) {
    return { uploads, results: [], locationRows: [], providerSummaries: {}, grandTotal: 0 };
  }

  const calcIds = calcResults.map(c => c.id);

  // 3. Batch-fetch location_results for all calculations
  const allLocations = await batchInQuery(
    supabase,
    'location_results',
    '*',
    'calculation_id',
    calcIds
  );

  // Group locations by calculation_id
  const locationsByCalc = {};
  (allLocations || []).forEach(loc => {
    if (!locationsByCalc[loc.calculation_id]) locationsByCalc[loc.calculation_id] = [];
    locationsByCalc[loc.calculation_id].push(loc);
  });

  // 4. Build flat location rows with provider/upload context
  const locationRows = [];
  const providerSummaries = {};
  let grandTotal = 0;

  for (const calc of calcResults) {
    const locations = locationsByCalc[calc.id] || [];
    const providerName = calc.providers?.name || 'Unknown Provider';
    const netTerms = calc.providers?.net_terms || null;
    const workWeekFrom = calc.uploads?.work_week_from_date;
    const workWeekTo = calc.uploads?.work_week_to_date;

    const workWeekLabel = workWeekFrom && workWeekTo
      ? `${format(getAdjustedDate(workWeekFrom), 'MM/dd/yyyy')} - ${format(getAdjustedDate(workWeekTo), 'MM/dd/yyyy')}`
      : '';

    for (const loc of locations) {
      locationRows.push({
        workWeek: workWeekLabel,
        providerName,
        orderNumbers: loc.order_numbers || '',
        customerName: loc.customer_name || 'N/A',
        locationName: loc.location_address || '',
        state: loc.state || 'N/A',
        serviceTypeName: loc.service_type_name || calc.service_type_name || 'Initial Service Call',
        rateType: loc.rate_type || calc.rate_type || 'FLAT',
        units: loc.units || 0,
        travelHours: Number(loc.travel_hours) || 0,
        onsiteHours: Number(loc.onsite_hours) || 0,
        billableHours: Number(loc.billable_hours) || 0,
        amount: loc.total_cost || 0,
        netTerms: netTerms ? `Net ${netTerms}` : '',
        payDate: calc.pay_date ? format(new Date(calc.pay_date), 'MM/dd/yyyy') : '',
        isExcluded: loc.is_excluded || false,
        exclusionReason: loc.exclusion_reason || ''
      });
    }

    // Provider summary
    if (!providerSummaries[providerName]) {
      providerSummaries[providerName] = { totalAmount: 0, locationCount: 0, unitCount: 0 };
    }
    providerSummaries[providerName].totalAmount += Number(calc.total_cost) || 0;
    providerSummaries[providerName].locationCount += Number(calc.location_count) || 0;
    providerSummaries[providerName].unitCount += Number(calc.total_units) || 0;

    grandTotal += Number(calc.total_cost) || 0;
  }

  return { uploads, results: calcResults, locationRows, providerSummaries, grandTotal };
};

/**
 * Generate CSV content from bulk export data.
 */
export const generateBulkCSV = (bulkData) => {
  const { locationRows, providerSummaries, grandTotal } = bulkData;

  const hasHourly = locationRows.some(r =>
    r.rateType === 'HOURLY' || r.travelHours > 0 || r.onsiteHours > 0 || r.billableHours > 0
  );

  const headers = [
    'Work Week', 'Provider', 'Order Numbers', 'Customer Name', 'Location', 'State',
    'Service Type', 'Rate Type', 'Units'
  ];
  if (hasHourly) headers.push('Travel Hours', 'Onsite Hours', 'Billable Hours');
  headers.push('Amount', 'Net Terms', 'Pay Date', 'Excluded', 'Exclusion Reason');

  const rows = locationRows.map(r => {
    const row = [
      r.workWeek, r.providerName, r.orderNumbers, r.customerName,
      r.locationName, r.state, r.serviceTypeName, r.rateType, r.units
    ];
    if (hasHourly) {
      row.push(
        r.travelHours > 0 ? r.travelHours.toFixed(2) : '',
        r.onsiteHours > 0 ? r.onsiteHours.toFixed(2) : '',
        r.billableHours > 0 ? r.billableHours.toFixed(2) : ''
      );
    }
    row.push(
      r.amount,
      r.netTerms,
      r.payDate,
      r.isExcluded ? 'Yes' : '',
      r.exclusionReason
    );
    return row;
  });

  // Blank separator row
  rows.push(Array(headers.length).fill(''));

  // Provider summary rows
  rows.push(['Provider Summary', ...Array(headers.length - 1).fill('')]);
  Object.entries(providerSummaries).forEach(([name, summary]) => {
    const summaryRow = ['', name, '', '', `${summary.locationCount} locations`, '', '', '', summary.unitCount];
    if (hasHourly) summaryRow.push('', '', '');
    summaryRow.push(`$${summary.totalAmount.toFixed(2)}`, '', '', '', '');
    rows.push(summaryRow);
  });

  // Grand total row
  rows.push(Array(headers.length).fill(''));
  const totalRow = ['Grand Total', ...Array(headers.length - 1).fill('')];
  const amountIdx = hasHourly ? 12 : 9;
  totalRow[amountIdx] = `$${grandTotal.toFixed(2)}`;
  rows.push(totalRow);

  const csvContent = [
    headers.map(escapeCSVField).join(','),
    ...rows.map(row => row.map(escapeCSVField).join(','))
  ].join('\n');

  return csvContent;
};

/**
 * Trigger a file download in the browser.
 */
export const downloadFile = (content, filename, mimeType = 'text/csv;charset=utf-8;') => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
